import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  type Node,
  type Edge,
  type Connection,
  type OnNodesChange,
  type OnEdgesChange,
  Panel,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { FlowNodeComponent } from '../flow/FlowNodeComponent';
import {
  draftGraphToReactFlow,
  newEdgeId,
  newNodeId,
  parseDraftGraph,
  reactFlowToDraftGraph,
  type AgentWorkflowDraftGraph,
} from './graphMapper';
import type { AgentWorkflowEdgeTypeDef, AgentWorkflowNodeTypeDef } from '../../services/hub.service';

const nodeTypes = { flowNode: FlowNodeComponent };

/** Agent Builder–style block types (labels shown on canvas). */
export const AGENT_WORKFLOW_BLOCK_TYPES = [
  { type: 'start', label: 'Start' },
  { type: 'agent', label: 'Agent' },
  { type: 'tool', label: 'Tool' },
  { type: 'condition', label: 'Condition' },
  { type: 'llm', label: 'LLM' },
  { type: 'rag', label: 'RAG' },
  { type: 'end', label: 'End' },
] as const;

export type AgentWorkflowBlockType = (typeof AGENT_WORKFLOW_BLOCK_TYPES)[number]['type'];

export interface AgentWorkflowCanvasProps {
  /** Raw jsonb from API — only re-applied when `graphSyncKey` changes */
  draftGraph: Record<string, unknown>;
  /** Change this after server load/save to reset canvas from `draftGraph` */
  graphSyncKey: string;
  onDraftGraphChange: (graph: AgentWorkflowDraftGraph) => void;
  availableNodeTypes?: AgentWorkflowNodeTypeDef[];
  availableEdgeTypes?: AgentWorkflowEdgeTypeDef[];
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function parseJsonSafe(input: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(input);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function AgentWorkflowCanvasInner({
  draftGraph,
  graphSyncKey,
  onDraftGraphChange,
  availableNodeTypes,
  availableEdgeTypes,
}: AgentWorkflowCanvasProps) {
  const parsed = parseDraftGraph(draftGraph);
  const initial = draftGraphToReactFlow(parsed);
  const [nodes, setNodes, onNodesChange] = useNodesState(initial.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initial.edges);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [jsonFieldErrors, setJsonFieldErrors] = useState<Record<string, string>>({});
  const metaRef = useRef(parsed.meta ?? {});
  const draftGraphRef = useRef(draftGraph);
  draftGraphRef.current = draftGraph;
  const { screenToFlowPosition } = useReactFlow();

  // Reload graph from props only when sync key changes (fetch / save refresh).
  // Do not depend on `draftGraph` identity — parent may pass a new object each render while editing.
  useEffect(() => {
    const nextParsed = parseDraftGraph(draftGraphRef.current);
    metaRef.current = nextParsed.meta ?? {};
    const next = draftGraphToReactFlow(nextParsed);
    setNodes(next.nodes);
    setEdges(next.edges);
  }, [graphSyncKey, setNodes, setEdges]);

  useEffect(() => {
    onDraftGraphChange(reactFlowToDraftGraph(nodes, edges, metaRef.current));
  }, [nodes, edges, onDraftGraphChange]);

  useEffect(() => {
    if (!availableNodeTypes?.length) return;
    setNodes((nds) =>
      nds.map((n) => {
        const data = isRecord(n.data) ? n.data : {};
        const backendType = typeof data.backendType === 'string' ? data.backendType : undefined;
        if (!backendType) return n;
        const def = availableNodeTypes.find((t) => t.type === backendType);
        if (!def) return n;
        return {
          ...n,
          data: {
            ...data,
            entrySlots: def.entrySlots ?? ['in'],
            outputSlots: def.outputSlots ?? ['out'],
          },
        };
      }),
    );
  }, [availableNodeTypes, setNodes]);

  const handleNodesChange: OnNodesChange = useCallback(
    (changes) => {
      onNodesChange(changes);
    },
    [onNodesChange],
  );

  const handleEdgesChange: OnEdgesChange = useCallback(
    (changes) => {
      onEdgesChange(changes);
    },
    [onEdgesChange],
  );

  const handleNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    setSelectedNodeId(node.id);
  }, []);

  const handlePaneClick = useCallback(() => {
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    setJsonFieldErrors({});
  }, []);

  const handleConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return;
      setEdges((eds) =>
        addEdge(
          {
            ...connection,
            id: newEdgeId(),
            data: {
              edgeType: 'default',
              config: {
                ...(typeof connection.sourceHandle === 'string'
                  ? { sourceSlot: connection.sourceHandle }
                  : {}),
                ...(typeof connection.targetHandle === 'string'
                  ? { targetSlot: connection.targetHandle }
                  : {}),
              },
            },
          },
          eds,
        ),
      );
    },
    [setEdges],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const blockType = event.dataTransfer.getData('application/agent-workflow-node-type');
      if (!blockType) return;
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      const defs = availableNodeTypes?.length
        ? availableNodeTypes.map((n) => ({ type: n.type, label: n.label }))
        : AGENT_WORKFLOW_BLOCK_TYPES;
      const def = defs.find((b) => b.type === blockType);
      const fullDef = availableNodeTypes?.find((n) => n.type === blockType);
      const label = def?.label ?? blockType;
      const id = newNodeId();
      setNodes((nds) => [
        ...nds,
        {
          id,
          type: 'flowNode',
          position,
          data: {
            label,
            backendType: blockType,
            config: null,
            entrySlots: fullDef?.entrySlots ?? ['in'],
            outputSlots: fullDef?.outputSlots ?? ['out'],
          },
        },
      ]);
    },
    [screenToFlowPosition, setNodes, availableNodeTypes],
  );

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const addBlock = useCallback(
    (blockType: string) => {
      const defs = availableNodeTypes?.length
        ? availableNodeTypes.map((n) => ({ type: n.type, label: n.label }))
        : AGENT_WORKFLOW_BLOCK_TYPES;
      const def = defs.find((b) => b.type === blockType);
      const fullDef = availableNodeTypes?.find((n) => n.type === blockType);
      const label = def?.label ?? blockType;
      const id = newNodeId();
      setNodes((nds) => [
        ...nds,
        {
          id,
          type: 'flowNode',
          position: { x: 200 + nds.length * 24, y: 120 + nds.length * 18 },
          data: {
            label,
            backendType: blockType,
            config: null,
            entrySlots: fullDef?.entrySlots ?? ['in'],
            outputSlots: fullDef?.outputSlots ?? ['out'],
          },
        },
      ]);
    },
    [setNodes, availableNodeTypes],
  );

  const paletteDefs = availableNodeTypes?.length
    ? availableNodeTypes.map((n) => ({ type: n.type, label: n.label }))
    : AGENT_WORKFLOW_BLOCK_TYPES;

  const selectedNode = useMemo(
    () => (selectedNodeId ? nodes.find((n) => n.id === selectedNodeId) ?? null : null),
    [nodes, selectedNodeId],
  );
  const selectedEdge = useMemo(
    () => (selectedEdgeId ? edges.find((e) => e.id === selectedEdgeId) ?? null : null),
    [edges, selectedEdgeId],
  );

  const selectedNodeType = useMemo(() => {
    const backendType = selectedNode?.data && isRecord(selectedNode.data)
      ? selectedNode.data.backendType
      : null;
    if (typeof backendType !== 'string') return null;
    return availableNodeTypes?.find((n) => n.type === backendType) ?? null;
  }, [selectedNode, availableNodeTypes]);

  const schemaProps = useMemo(() => {
    const schema = selectedNodeType?.configSchema;
    if (!isRecord(schema)) return {};
    const properties = schema.properties;
    if (!isRecord(properties)) return {};
    return properties;
  }, [selectedNodeType]);

  const selectedConfig = useMemo(() => {
    if (!selectedNode || !isRecord(selectedNode.data)) return {};
    const cfg = selectedNode.data.config;
    return isRecord(cfg) ? cfg : {};
  }, [selectedNode]);

  const updateSelectedNodeConfigField = useCallback(
    (field: string, value: unknown) => {
      if (!selectedNodeId) return;
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id !== selectedNodeId) return n;
          const currentData = isRecord(n.data) ? n.data : {};
          const currentConfig = isRecord(currentData.config) ? currentData.config : {};
          const nextConfig = { ...currentConfig, [field]: value };
          const summaryCandidate =
            typeof nextConfig.name === 'string' && nextConfig.name.trim()
              ? nextConfig.name
              : typeof nextConfig.instructions === 'string' && nextConfig.instructions.trim()
                ? nextConfig.instructions
                : undefined;
          const preview = summaryCandidate ? String(summaryCandidate).slice(0, 48) : currentData.preview;
          return {
            ...n,
            data: {
              ...currentData,
              config: nextConfig,
              preview,
            },
          };
        }),
      );
    },
    [selectedNodeId, setNodes],
  );

  return (
    <div className="h-full w-full min-h-[420px]" onDrop={handleDrop} onDragOver={handleDragOver}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={handleConnect}
        onNodeClick={handleNodeClick}
        onPaneClick={handlePaneClick}
        onEdgeClick={(_e, edge) => {
          setSelectedNodeId(null);
          setSelectedEdgeId(edge.id);
        }}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.2}
        maxZoom={1.5}
        defaultEdgeOptions={{ type: 'smoothstep' }}
        deleteKeyCode={['Backspace', 'Delete']}
      >
        <Background />
        <Controls />
        <MiniMap />
        <Panel
          position="top-left"
          className="bg-white/95 rounded-xl shadow-lg border border-gray-200 overflow-hidden max-h-[85vh] overflow-y-auto"
        >
          <div className="p-2.5 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Workflow nodes</p>
            <p className="text-[11px] text-gray-500 mt-0.5">Drag onto canvas or click to add</p>
          </div>
          <div className="p-2 flex flex-wrap gap-1">
            {paletteDefs.map(({ type, label }) => (
              <button
                key={type}
                type="button"
                className="px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('application/agent-workflow-node-type', type);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onClick={() => addBlock(type)}
                title={`Add ${label}`}
              >
                {label}
              </button>
            ))}
          </div>
        </Panel>
        {selectedNode && (
          <Panel
            position="top-right"
            className="bg-white/95 rounded-xl shadow-lg border border-gray-200 overflow-hidden max-h-[85vh] overflow-y-auto w-[360px]"
          >
            <div className="p-3 border-b border-gray-100 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Node config</p>
                <p className="text-[11px] text-gray-500 mt-0.5">{String(selectedNodeType?.label ?? 'Node')}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedNodeId(null)}
                className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
            <div className="p-3 space-y-3">
              {selectedNodeType ? (
                <div className="rounded-md border border-gray-200 bg-gray-50 p-2 text-[11px] text-gray-600 space-y-1">
                  <p>
                    <span className="font-medium">Entry slots:</span>{' '}
                    {(selectedNodeType.entrySlots ?? ['in']).join(', ') || '-'}
                  </p>
                  <p>
                    <span className="font-medium">Output slots:</span>{' '}
                    {(selectedNodeType.outputSlots ?? ['out']).join(', ') || '-'}
                  </p>
                  <p>
                    <span className="font-medium">Input schema:</span>{' '}
                    {selectedNodeType.inputSchema ? 'defined' : 'none'}
                  </p>
                  <p>
                    <span className="font-medium">Output schema:</span>{' '}
                    {selectedNodeType.outputSchema ? 'defined' : 'none'}
                  </p>
                </div>
              ) : null}
              {Object.keys(schemaProps).length === 0 ? (
                <p className="text-xs text-gray-500">No schema available for this node type.</p>
              ) : (
                Object.entries(schemaProps).map(([field, spec]) => {
                  const fieldSpec = isRecord(spec) ? spec : {};
                  const fieldType = typeof fieldSpec.type === 'string' ? fieldSpec.type : 'string';
                  const title = typeof fieldSpec.title === 'string' ? fieldSpec.title : field;
                  const enumValues = Array.isArray(fieldSpec.enum)
                    ? fieldSpec.enum.filter((v): v is string => typeof v === 'string')
                    : [];
                  const value = selectedConfig[field];

                  if (enumValues.length > 0) {
                    return (
                      <label key={field} className="block">
                        <span className="text-xs font-medium text-gray-600">{title}</span>
                        <select
                          value={typeof value === 'string' ? value : ''}
                          onChange={(e) => updateSelectedNodeConfigField(field, e.target.value)}
                          className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                        >
                          <option value="">Select...</option>
                          {enumValues.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      </label>
                    );
                  }

                  if (fieldType === 'boolean') {
                    return (
                      <label key={field} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={Boolean(value)}
                          onChange={(e) => updateSelectedNodeConfigField(field, e.target.checked)}
                          className="rounded border-gray-300 text-blue-600"
                        />
                        <span className="text-xs font-medium text-gray-700">{title}</span>
                      </label>
                    );
                  }

                  if (fieldType === 'number' || fieldType === 'integer') {
                    return (
                      <label key={field} className="block">
                        <span className="text-xs font-medium text-gray-600">{title}</span>
                        <input
                          type="number"
                          value={typeof value === 'number' ? value : ''}
                          onChange={(e) =>
                            updateSelectedNodeConfigField(
                              field,
                              e.target.value === '' ? null : Number(e.target.value),
                            )
                          }
                          className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                        />
                      </label>
                    );
                  }

                  if (fieldType === 'array') {
                    const lines = Array.isArray(value)
                      ? value.filter((v): v is string => typeof v === 'string').join('\n')
                      : '';
                    return (
                      <label key={field} className="block">
                        <span className="text-xs font-medium text-gray-600">{title}</span>
                        <textarea
                          value={lines}
                          onChange={(e) =>
                            updateSelectedNodeConfigField(
                              field,
                              e.target.value
                                .split('\n')
                                .map((v) => v.trim())
                                .filter(Boolean),
                            )
                          }
                          rows={3}
                          className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                          placeholder="One item per line"
                        />
                      </label>
                    );
                  }

                  if (fieldType === 'object') {
                    const text = value && typeof value === 'object' ? JSON.stringify(value, null, 2) : '{}';
                    return (
                      <label key={field} className="block">
                        <span className="text-xs font-medium text-gray-600">{title}</span>
                        <textarea
                          value={text}
                          onChange={(e) => {
                            const parsed = parseJsonSafe(e.target.value);
                            if (!parsed) {
                              setJsonFieldErrors((prev) => ({ ...prev, [field]: 'Invalid JSON object' }));
                              return;
                            }
                            setJsonFieldErrors((prev) => {
                              const next = { ...prev };
                              delete next[field];
                              return next;
                            });
                            updateSelectedNodeConfigField(field, parsed);
                          }}
                          rows={6}
                          className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm font-mono"
                        />
                        {jsonFieldErrors[field] ? (
                          <p className="mt-1 text-[11px] text-red-600">{jsonFieldErrors[field]}</p>
                        ) : null}
                      </label>
                    );
                  }

                  const longText = field.toLowerCase().includes('instruction') || field.toLowerCase().includes('context');
                  if (longText) {
                    return (
                      <label key={field} className="block">
                        <span className="text-xs font-medium text-gray-600">{title}</span>
                        <textarea
                          value={typeof value === 'string' ? value : ''}
                          onChange={(e) => updateSelectedNodeConfigField(field, e.target.value)}
                          rows={4}
                          className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                        />
                      </label>
                    );
                  }

                  return (
                    <label key={field} className="block">
                      <span className="text-xs font-medium text-gray-600">{title}</span>
                      <input
                        type="text"
                        value={typeof value === 'string' ? value : ''}
                        onChange={(e) => updateSelectedNodeConfigField(field, e.target.value)}
                        className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                      />
                    </label>
                  );
                })
              )}
            </div>
          </Panel>
        )}
        {selectedEdge && (
          <Panel
            position="bottom-right"
            className="bg-white/95 rounded-xl shadow-lg border border-gray-200 overflow-hidden w-[360px]"
          >
            <div className="p-3 border-b border-gray-100 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Edge config</p>
                <p className="text-[11px] text-gray-500 mt-0.5">{selectedEdge.source} → {selectedEdge.target}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedEdgeId(null)}
                className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
            <div className="p-3 space-y-3">
              <label className="block">
                <span className="text-xs font-medium text-gray-600">Type</span>
                <select
                  value={typeof (selectedEdge.data as Record<string, unknown> | undefined)?.edgeType === 'string' ? String((selectedEdge.data as Record<string, unknown>).edgeType) : 'default'}
                  onChange={(e) => {
                    const edgeType = e.target.value;
                    setEdges((prev) =>
                      prev.map((ed) =>
                        ed.id === selectedEdge.id
                          ? { ...ed, data: { ...((ed.data as Record<string, unknown>) ?? {}), edgeType } }
                          : ed,
                      ),
                    );
                  }}
                  className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                >
                  {(availableEdgeTypes?.length
                    ? availableEdgeTypes
                    : [
                        { type: 'default', label: 'default' },
                        { type: 'condition', label: 'condition' },
                        { type: 'fallback', label: 'fallback' },
                        { type: 'error', label: 'error' },
                      ]
                  ).map((t) => (
                    <option key={t.type} value={t.type}>
                      {t.type}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-600">Source slot</span>
                <input
                  type="text"
                  value={
                    typeof ((selectedEdge.data as Record<string, unknown> | undefined)?.config as Record<string, unknown> | undefined)?.sourceSlot === 'string'
                      ? String((((selectedEdge.data as Record<string, unknown>).config as Record<string, unknown>).sourceSlot))
                      : ''
                  }
                  onChange={(e) => {
                    const sourceSlot = e.target.value;
                    setEdges((prev) =>
                      prev.map((ed) =>
                        ed.id === selectedEdge.id
                          ? {
                              ...ed,
                              sourceHandle: sourceSlot || undefined,
                              data: {
                                ...((ed.data as Record<string, unknown>) ?? {}),
                                config: {
                                  ...(((ed.data as Record<string, unknown>)?.config as Record<string, unknown>) ?? {}),
                                  sourceSlot,
                                },
                              },
                            }
                          : ed,
                      ),
                    );
                  }}
                  className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-600">Target slot</span>
                <input
                  type="text"
                  value={
                    typeof ((selectedEdge.data as Record<string, unknown> | undefined)?.config as Record<string, unknown> | undefined)?.targetSlot === 'string'
                      ? String((((selectedEdge.data as Record<string, unknown>).config as Record<string, unknown>).targetSlot))
                      : ''
                  }
                  onChange={(e) => {
                    const targetSlot = e.target.value;
                    setEdges((prev) =>
                      prev.map((ed) =>
                        ed.id === selectedEdge.id
                          ? {
                              ...ed,
                              targetHandle: targetSlot || undefined,
                              data: {
                                ...((ed.data as Record<string, unknown>) ?? {}),
                                config: {
                                  ...(((ed.data as Record<string, unknown>)?.config as Record<string, unknown>) ?? {}),
                                  targetSlot,
                                },
                              },
                            }
                          : ed,
                      ),
                    );
                  }}
                  className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-600">Priority</span>
                <input
                  type="number"
                  value={typeof (selectedEdge.data as Record<string, unknown> | undefined)?.priority === 'number' ? Number((selectedEdge.data as Record<string, unknown>).priority) : ''}
                  onChange={(e) => {
                    const priority = e.target.value === '' ? null : Number(e.target.value);
                    setEdges((prev) =>
                      prev.map((ed) =>
                        ed.id === selectedEdge.id
                          ? { ...ed, data: { ...((ed.data as Record<string, unknown>) ?? {}), priority } }
                          : ed,
                      ),
                    );
                  }}
                  className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-600">Condition expression</span>
                <input
                  type="text"
                  value={typeof ((selectedEdge.data as Record<string, unknown> | undefined)?.config as Record<string, unknown> | undefined)?.expression === 'string' ? String((((selectedEdge.data as Record<string, unknown>).config as Record<string, unknown>).expression)) : ''}
                  onChange={(e) => {
                    const expression = e.target.value;
                    setEdges((prev) =>
                      prev.map((ed) =>
                        ed.id === selectedEdge.id
                          ? {
                              ...ed,
                              data: {
                                ...((ed.data as Record<string, unknown>) ?? {}),
                                config: {
                                  ...(((ed.data as Record<string, unknown>)?.config as Record<string, unknown>) ?? {}),
                                  expression,
                                },
                              },
                            }
                          : ed,
                      ),
                    );
                  }}
                  className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                />
              </label>
            </div>
          </Panel>
        )}
      </ReactFlow>
    </div>
  );
}

export function AgentWorkflowCanvas(props: AgentWorkflowCanvasProps) {
  return (
    <ReactFlowProvider>
      <AgentWorkflowCanvasInner {...props} />
    </ReactFlowProvider>
  );
}
